<template>
  <BaseDialog
    contentClass="extraction-documents"
    ref="dialog"
    sidePanel
    :persistent="hasSelection"
    @closeModal="closeDialog"
  >
    <template slot="title">{{ $t('extraction-documents-dialog.title') }}</template>
    <template slot="content">
      <div class="extraction-documents-dialog">
        <div class="extraction-documents-dialog__list-container">
          <div class="extraction-documents-dialog__list-header">
            <div class="extraction-documents-dialog__select-all">
              <FormCheckbox
                :value="areAllSelected"
                :indeterminate="areSomeSelected && !areAllSelected"
                :disabled="isSelectAllDisabled"
                size="small"
                @input="toggleSelectAll"
              />
            </div>
            <span class="extraction-documents-dialog__selected-count">
              {{ selectedDocumentIds.length }} / {{ MAX_SELECTED }}
            </span>
          </div>

          <div class="extraction-documents-dialog__list">
            <div
              v-for="document in documents"
              :key="document.id"
              class="extraction-documents-dialog__row"
              :class="{
                'is-selected': isSelected(document.id),
                'is-disabled': isRowDisabled(document.id),
              }"
              @click="onRowClick(document.id)"
            >
              <div class="extraction-documents-dialog__row-checkbox" @click.stop>
                <FormCheckbox
                  :value="isSelected(document.id)"
                  :disabled="isRowDisabled(document.id)"
                  size="small"
                  @input="toggleDocument(document.id)"
                />
              </div>
              <div class="extraction-documents-dialog__row-icon">
                <BaseIcon :name="getDocumentIcon(document)" />
              </div>
              <div class="extraction-documents-dialog__row-content">
                <span class="extraction-documents-dialog__row-name" :class="{ 'is-selected': isSelected(document.id) }">
                  {{ document.name }}
                </span>
                <span class="extraction-documents-dialog__row-meta">
                  {{ getDocumentMeta(document) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isLimitReached" class="extraction-documents-dialog__limit-message">
          {{ $t('extraction-documents-dialog.selection-limit', { limit: MAX_SELECTED }) }}
        </div>

        <div class="extraction-documents-dialog__warning">
          <BaseIcon name="alert-triangle" class="extraction-documents-dialog__warning-icon" />
          <span class="extraction-documents-dialog__warning-text">
            {{ $t('extraction-documents-dialog.ai-warning') }}
          </span>
        </div>

        <div class="extraction-documents-dialog__footer">
          <BaseButton is-transparent is-small @click.native="closeDialog">{{ $t('actions.cancel') }}</BaseButton>
          <span
            v-tooltip="{
              content: isLimitExceeded
                ? $t('extraction-documents-dialog.selection-limit', { limit: MAX_SELECTED })
                : '',
              placement: 'top',
              offset: '8px',
            }"
          >
            <FormButton :disabled="!hasSelection || isLimitExceeded" @click.native="onSubmit">
              <div class="extraction-documents-dialog__submit-icon">
                <BaseIcon name="ai" color="white" />
                <span>{{ $t('extraction-documents-dialog.submit') }}</span>
              </div>
            </FormButton>
          </span>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import FormButton from '@/components/form/FormButton';
import FormCheckbox from '@/components/form/FormCheckbox';
import { mapGetters } from 'vuex';
import { getDocumentIcon } from '@/util/document-file-util';
import BaseButton from '@/components/base/BaseButton.vue';

const MAX_SELECTED = 15;

export default {
  name: 'ExtractionDocumentsDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    FormButton,
    FormCheckbox,
  },
  data() {
    return {
      selectedDocumentIds: [],
    };
  },
  computed: {
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('fieldsExtraction', ['previousDocumentIds']),
    MAX_SELECTED() {
      return MAX_SELECTED;
    },
    documents() {
      return this.opportunity && this.opportunity.documents ? this.opportunity.documents : [];
    },
    areAllSelected() {
      return this.documents.length > 0 && this.selectedDocumentIds.length === this.documents.length;
    },
    areSomeSelected() {
      return this.selectedDocumentIds.length > 0;
    },
    hasSelection() {
      return this.selectedDocumentIds.length > 0;
    },
    isLimitReached() {
      return this.selectedDocumentIds.length >= MAX_SELECTED;
    },
    isLimitExceeded() {
      return this.selectedDocumentIds.length > MAX_SELECTED;
    },
    isSelectAllDisabled() {
      return !this.areAllSelected && this.documents.length > MAX_SELECTED;
    },
  },
  methods: {
    getDocumentIcon,
    openDialog() {
      this.selectedDocumentIds = [...this.previousDocumentIds];
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      this.selectedDocumentIds = [];
      this.$refs.dialog.isModalOpen = false;
    },
    isSelected(documentId) {
      return this.selectedDocumentIds.includes(documentId);
    },
    isRowDisabled(documentId) {
      return this.isLimitReached && !this.isSelected(documentId);
    },
    onRowClick(documentId) {
      if (this.isRowDisabled(documentId)) return;
      this.toggleDocument(documentId);
    },
    toggleDocument(documentId) {
      const index = this.selectedDocumentIds.indexOf(documentId);
      if (index > -1) {
        this.selectedDocumentIds.splice(index, 1);
      } else if (this.selectedDocumentIds.length < MAX_SELECTED) {
        this.selectedDocumentIds.push(documentId);
      }
    },
    toggleSelectAll() {
      if (this.areAllSelected) {
        this.selectedDocumentIds = [];
      } else if (!this.isSelectAllDisabled) {
        this.selectedDocumentIds = this.documents.map((doc) => doc.id);
      }
    },
    getDocumentMeta(document) {
      const parts = [];
      if (document.pageCount) {
        parts.push(`${document.pageCount} pages`);
      }
      if (document.size) {
        parts.push(this.formatFileSize(document.size));
      }
      return parts.join(' \u2022 ');
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    onSubmit() {
      const selectedDocuments = this.documents.filter((doc) => this.selectedDocumentIds.includes(doc.id));
      this.$emit('submit', selectedDocuments);
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
$extraction-ai-color: $color-accent-dark;
$extraction-ai-bg: rgba($extraction-ai-color, 0.06);

.base-dialog__extraction-documents {
  .base-dialog__content {
    padding: 0;
    background: $color-white !important;
  }
}

.extraction-documents-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 8px 8px;

  &__list-container {
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    min-height: 0;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    overflow: hidden;
    background: $color-white;
  }

  &__list-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px 4px 9px;
    border-bottom: 1px solid $color-border;
  }

  &__select-all {
    .form-checkbox {
      padding: 0;
      margin: 0;
    }

    .form-checkbox__check {
      height: 16px;
      width: 16px;

      .feather {
        height: 12px;
        width: 12px;
      }
    }
  }

  &__selected-count {
    font-size: $font-size-xxsmall;
    font-weight: 600;
    color: $color-normal-pale;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    overflow-y: auto;
    min-height: 0;
  }

  &__limit-message {
    padding: 8px 4px 0;
    font-size: $font-size-xsmall;
    line-height: 1.2;
    color: $color-yellow;
  }

  &__row {
    display: flex;
    align-items: center;
    padding: 5px 8px 5px 6px;
    border-radius: $border-radius-medium;
    cursor: pointer;
    transition: background-color 150ms ease;

    &:hover:not(.is-selected):not(.is-disabled) {
      background-color: $color-neutral-pale;
    }

    &.is-selected {
      background-color: $extraction-ai-bg;
      border: 1px solid $color-neutral-pale;
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .form-checkbox {
      padding: 0;
      margin: 0;
    }

    .form-checkbox__check {
      height: 16px;
      width: 16px;

      .feather {
        height: 12px;
        width: 12px;
      }
    }
  }

  &__row-checkbox {
    flex-shrink: 0;
    margin-right: 8px;
  }

  &__row-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin: 0 8px 0 0;

    .feather {
      width: 18px;
      height: 18px;
      color: $color-normal-pale;
    }
  }

  &__row-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  &__row-name {
    font-size: 11px;
    line-height: 1.2;
    color: $color-normal-pale;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-selected {
      font-weight: 600;
      color: $color-normal;
    }
  }

  &__row-meta {
    font-size: $font-size-xxsmall;
    line-height: 1;
    color: $color-normal-pale;
  }

  &__warning {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px;
    margin-top: auto;
  }

  &__warning-icon {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    color: $color-yellow;
  }

  &__warning-text {
    font-size: $font-size-xxsmall;
    line-height: 1.2;
    color: $color-normal;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 0 8px 8px;
  }

  &__submit-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
}
</style>
